import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import type { ReconcileEnrichmentJobResponse } from '~/features/list-archive/types/list-archive.types';
import { extractApiError } from '~/utils/extract-api-error';

import { reconcileEnrichmentJob } from '../services/catalog-operations.service';

/**
 * Repairs a run stuck at IN_PROGRESS (`POST jobs/{id}/reconcile`). A
 * `reconciled: true` answer means the status moved to FAILED — lists, tab
 * counts and the detail header all read that column, so the whole subtree is
 * invalidated. `reconciled: false` is still a success: the server's `detail`
 * explains why nothing changed (the run really is executing, Temporal was
 * unreachable, or the status was already settled), and we relay it verbatim.
 */
export function useReconcileEnrichmentJob() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  return useMutation<ReconcileEnrichmentJobResponse, unknown, string>({
    mutationFn: (jobId) => reconcileEnrichmentJob(jobId),
    onSuccess: (result, jobId) => {
      if (!result.reconciled) {
        toast.info(t('listArchive.reconcile.unchanged', { detail: result.detail }));
        return;
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalogOperations.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.feedAudit.detail(jobId) });
      toast.success(t('listArchive.reconcile.fixed'));
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('listArchive.reconcile.error')));
    },
  });
}
